import React, { useState } from 'react';
import { Dialog } from '@mui/material';
import { Close, SwapHoriz, CheckCircle } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/sv';

interface ChangeTenantModalProps {
  open: boolean;
  onClose: () => void;
  currentStartDate: string;
  currentEndDate: string;
  currentTenantName: string;
  apartmentNumber: string;
  address: string;
  onConfirm: (newStartDate: string, newEndDate: string) => Promise<void>;
}

const ChangeTenantModal: React.FC<ChangeTenantModalProps> = ({
  open,
  onClose,
  currentStartDate,
  currentEndDate,
  currentTenantName,
  apartmentNumber,
  address,
  onConfirm
}) => {
  const [newStartDate, setNewStartDate] = useState<Dayjs | null>(dayjs());
  const [newEndDate, setNewEndDate] = useState<Dayjs | null>(dayjs(currentEndDate));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleConfirm = async () => {
    if (!newStartDate || !newEndDate) {
      setError('Vänligen välj både start- och slutdatum');
      return;
    }

    if (newEndDate.isBefore(newStartDate)) {
      setError('Slutdatum måste vara efter startdatum');
      return;
    }

    if (newStartDate.isBefore(dayjs())) {
      setError('Startdatum kan inte vara i det förflutna');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      // Datum, inte tidpunkter — servern normaliserar ändå, men det ska inte behövas.
      await onConfirm(newStartDate.format('YYYY-MM-DD'), newEndDate.format('YYYY-MM-DD'));
      setSuccess(true);

      // Stäng modal efter 2 sekunder
      setTimeout(() => {
        onClose();
        setSuccess(false);
      }, 2000);
    } catch (err) {
      console.error('Error changing tenant:', err);
      setError('Ett fel uppstod när bytet skulle genomföras. Försök igen.');
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      setError(null);
      setSuccess(false);
    }
  };

  // Visa bekräftelse om lyckad
  if (success) {
    return (
      <Dialog open={open} className="fixed z-50 inset-0 flex items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="relative bg-white rounded-2xl shadow-lg w-full max-w-md mx-auto p-8 text-center">
          <div className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center bg-green-100">
            <CheckCircle sx={{ fontSize: 48, color: '#22c55e' }} />
          </div>

          <h2 className="text-xl font-bold text-[#222222] font-['Poppins'] mb-2">
            Ny ansökan skapad!
          </h2>

          <p className="text-sm text-[#626262]">
            Nu kan du lägga till information om den nya hyresgästen. Du kommer att omdirigeras till nästa steg.
          </p>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      className="fixed z-50 inset-0 flex items-center justify-center p-4"
      PaperProps={{
        style: { backgroundColor: 'transparent', boxShadow: 'none', overflow: 'visible' }
      }}
    >
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="relative bg-white rounded-2xl shadow-lg w-full max-w-6xl mx-auto max-h-[90vh] flex flex-col">
        <button
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-zinc-100 text-zinc-500 z-10"
          onClick={handleClose}
          disabled={loading}
          aria-label="Stäng"
        >
          <Close className="w-5 h-5" />
        </button>

        <div className="overflow-y-auto p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Header */}
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
              <SwapHoriz sx={{ fontSize: 24, color: '#10B981' }} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-[#222222] font-['Poppins']">
                Byt hyresgäst
              </h2>
              <p className="text-sm text-[#626262]">
                {address} {apartmentNumber && `Lgh ${apartmentNumber}`}
              </p>
            </div>
          </div>

          {/* Varning */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-900 font-semibold mb-2">
              ⚠️ Viktigt att veta
            </p>
            <ul className="text-sm text-yellow-800 space-y-1 ml-4 list-disc">
              <li>Nuvarande uthyrning till <strong>{currentTenantName}</strong> kommer att avslutas</li>
              <li>Du kommer att omdirigeras för att fylla i den nya hyresgästens uppgifter</li>
              <li>Kreditkontroll och BRF-godkännande krävs för den nya hyresgästen</li>
            </ul>
          </div>

          {/* Nuvarande information */}
          <div className="p-4 bg-gray-50 rounded-lg space-y-2">
            <p className="text-xs font-semibold text-[#626262] mb-2">Nuvarande uthyrning:</p>
            <div className="flex justify-between text-sm">
              <span className="text-[#626262]">Hyresgäst:</span>
              <span className="font-semibold text-[#222222]">{currentTenantName}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[#626262]">Period:</span>
              <span className="font-semibold text-[#222222]">
                {dayjs(currentStartDate).format('YYYY-MM-DD')} – {dayjs(currentEndDate).format('YYYY-MM-DD')}
              </span>
            </div>
          </div>

          {/* Ny period */}
          <div className="space-y-4">
            <h3 className="font-semibold text-[#222222]">Välj period för ny hyresgäst</h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-[#222222] mb-2">
                  Startdatum *
                </label>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="sv">
                  <DatePicker
                    value={newStartDate}
                    onChange={(date) => setNewStartDate(date)}
                    minDate={dayjs()}
                    label="Välj startdatum"
                    format="YYYY-MM-DD"
                    slotProps={{
                      textField: {
                        fullWidth: true
                      }
                    }}
                  />
                </LocalizationProvider>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#222222] mb-2">
                  Slutdatum *
                </label>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="sv">
                  <DatePicker
                    value={newEndDate}
                    onChange={(date) => setNewEndDate(date)}
                    minDate={newStartDate || dayjs()}
                    label="Välj slutdatum"
                    format="YYYY-MM-DD"
                    slotProps={{
                      textField: {
                        fullWidth: true
                      }
                    }}
                  />
                </LocalizationProvider>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="border-t border-[#E2EAF0] pt-4 mt-4">
            <div className="flex gap-2">
              <button
                onClick={handleConfirm}
                disabled={loading || !newStartDate || !newEndDate}
                className="flex-1 bg-green-500 text-white font-bold py-2.5 px-4 rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                {loading ? '⏳ Skapar ny ansökan...' : '✓ Byt hyresgäst'}
              </button>
              <button
                onClick={handleClose}
                disabled={loading}
                className="flex-1 border border-gray-300 text-gray-700 font-bold py-2.5 px-4 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 text-sm"
              >
                Avbryt
              </button>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default ChangeTenantModal;
